import { skipToken } from "@reduxjs/toolkit/query";
import { useEffect } from "react";

import { useModelRevisions } from "apis/procore/modelRevisions";
import { useGetMiniMapDataQuery } from "apis/procore/procoreApi";
import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { selectProcoreContext } from "slices/explorer";
import { AsyncStatus } from "types/misc";
import { log } from "utils/log";

import { downloadDrawingData } from "./downloadDrawingData";
import { selectCurrentDrawing } from "./minimapSlice";

const l = log.miniMap.utils;

/**
 * Subscribe to the mini map data query for the active scene.
 *
 * Implementation notes:
 *
 *   1. Two-query chain: the shared `useModelRevisions` query resolves the
 *      revision ID for the opened scene, then `useGetMiniMapDataQuery` fetches
 *      plans → locations → drawings for that revision. Both use `skipToken`
 *      until their inputs are ready. The sequential dependency (revision before
 *      plans) is inherent — plans require a revision ID — so the two-query
 *      structure reflects reality rather than adding a waterfall.
 *
 *   2. The slice's `extraReducers` matcher mirrors the `getMiniMapData` cache
 *      into slice state, so existing selectors keep working without changes.
 *
 *   3. The per-drawing image for the *current* drawing is downloaded here
 *      because `handleCameraUpdated` (in `miniMapWeb` / `miniMapMobile`)
 *      only kicks off a fetch when the *target* drawing differs from the
 *      current one, so it never fetches the drawing that is already current
 *      on mount / re-enable. The download is keyed on the *slice's* current
 *      drawing (`selectCurrentDrawing`) — NOT `data.currentDrawingId` from the
 *      query payload. Those two diverge: the camera handler repoints the slice
 *      current drawing via `setCurrentDrawing` when the user changes level,
 *      including while the mini map is hidden (where it sets the current
 *      drawing but skips the download because `enabled` is false). On
 *      re-enable the camera handler then sees `target === current` and skips
 *      the download, so this effect must download whatever drawing is actually
 *      current — otherwise the displayed drawing stays on the loading shimmer
 *      forever (BM-11644).
 *
 *      The download is driven off the current drawing's *status*: it only kicks
 *      off from `Initial`, transitions it to `Loading`, then `Success`/`Error`.
 *      Depending on the status (rather than a ref) is what makes
 *      `forceMiniMapRefresh` work: the matcher resets every drawing's
 *      `asyncState` back to `Initial` on each fulfilled fetch, and when the
 *      current drawing keeps the same id across the refresh only the status
 *      changes (Success → Initial). Keying on status re-runs the effect and
 *      re-downloads; keying only on id/url would leave it stranded on the
 *      shimmer forever.
 *
 * Lives in its own file (rather than `miniMapUtils.tsx`) so that pure-utility
 * consumers — and their tests — do not transitively pull in `procoreApi`,
 * which would create a circular import via `procoreClient → app/store`.
 *
 * @param enabled When `false`, the per-drawing image download is suppressed.
 *   Callers pass the mini map visibility config here so that a hidden mini map
 *   does not download its drawing. The lightweight metadata queries keep
 *   running so the slice stays populated; this — together with the
 *   already-downloaded guard below — preserves the BM-7305 behavior where a
 *   drawing image survives a visibility toggle without re-fetching.
 */
export const useMiniMapData = (enabled = true) => {
    const procoreContext = useAppSelector(selectProcoreContext);
    const dispatch = useAppDispatch();
    const currentDrawing = useAppSelector(selectCurrentDrawing);

    // Shared, session-cached revisions query (reused across the app).
    const { data: revisions } = useModelRevisions();
    const modelRevisionId = revisions?.[0]?.id;

    // Keep the query subscribed so the slice stays mirrored via the matcher.
    // The download below is driven off the slice's current drawing, not this
    // query's payload (see implementation note 3).
    useGetMiniMapDataQuery(
        procoreContext?.companyId && procoreContext.projectId && procoreContext.sceneVersionId && modelRevisionId
            ? { companyId: procoreContext.companyId, projectId: procoreContext.projectId, modelRevisionId }
            : skipToken,
    );

    // Download the drawing that is actually current in the slice. Keyed on the
    // identity (id + url) and the *preview* status so the effect re-runs both
    // when the current drawing changes and when its status is reset back to
    // `Initial` (e.g. by `forceMiniMapRefresh`, which re-mirrors the query
    // payload and resets every drawing's async states). The preview status is
    // authoritative here because the mini map only consumes the preview and
    // `downloadDrawingData` moves both states out of `Initial` together.
    const currentDrawingId = currentDrawing?.id;
    const currentDrawingUrl = currentDrawing?.drawingData.url;
    const currentDrawingStatus = currentDrawing?.drawingData.previewAsyncState.status;

    useEffect(() => {
        // Don't download the drawing image when the mini map is turned off.
        if (!enabled) {
            return;
        }
        if (!currentDrawingId || !currentDrawingUrl) {
            return;
        }

        // Only kick off a download from `Initial`. Once we set `Loading` (and
        // later `Success`/`Error`) this effect re-runs but no-ops here, so it
        // never restarts an in-flight or completed download — while a reset back
        // to `Initial` (forceMiniMapRefresh) does re-trigger it.
        if (currentDrawingStatus !== AsyncStatus.Initial) {
            return;
        }

        downloadDrawingData({
            dispatch,
            drawingId: currentDrawingId,
            url: currentDrawingUrl,
            telemetrySource: "miniMap.useMiniMapData.fetchDrawingData",
        }).then(() => {
            l.debug("Finished downloading drawing data for mini map:", currentDrawingId);
        });
    }, [currentDrawingId, currentDrawingUrl, currentDrawingStatus, dispatch, enabled]);
};
